/*
  pnx-complementary-purchase.js（Issue #8）
  補完商品「対応商品購入UI」の振る舞い。
  - 数量入力（0以上の整数に正規化）・行ごとのvariant選択（価格／在庫／画像の連動）
  - 数量1以上の行だけを1リクエストでまとめてカートへ追加（theme.js の ProductsBundle と同じ経路）
  - 価格の計算・表示はしない（価格DOMは Liquid の price snippet が描画し、Samita が上書きする）
  読み込み: snippets/pnx-complementary-purchase-list.liquid から defer で読む。
  二重定義ガード付き（Theme Editor の section 再読み込みで同じ script が再評価されても安全）。
*/
if (!customElements.get('pnx-complementary-purchase')) {
  class PnxComplementaryPurchase extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('keydown', this.handleKeydown);
      this.rows.forEach((row) => this.syncRow(row));
      this.refreshSubmit();
      this.setupWholesaleMirror();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('keydown', this.handleKeydown);
      this.teardownWholesaleMirror();
    }

    /* ---------- 卸価格（Samita）ミラー ----------
       Samita は商品ページモードで .product 内の価格DOMを本体商品の卸価格で書き換えるため、
       行の価格DOMには Samita のセレクタを持たせない。代わりに .product の外（body直下・不可視）へ
       variant ごとの「作業枠」（Samita 一覧モードの要素）を作り、Samita がそこへ書き込んだ卸価格HTMLを
       表示セルへコピーする。Samita の内部関数は呼ばない（属性契約と DOM 監視だけ）。 */

    setupWholesaleMirror() {
      if (!(window.Samita && window.Samita.Wholesale)) {
        this.querySelectorAll('[data-pnx-cp-price]').forEach((box) => this.setUnitState(box, 'no-custom'));
        return;
      }
      if (this.scratch) return;

      const scratch = document.createElement('div');
      scratch.className = 'pnx-cp-samita-scratch';
      scratch.setAttribute('aria-hidden', 'true');
      scratch.dataset.pnxCpScratchFor = this.id || '';

      this.rows.forEach((row) => {
        const handle = row.dataset.productHandle;
        const url = row.dataset.productUrl || (handle ? `/products/${handle}` : '');
        if (!handle) return;
        row.querySelectorAll('[data-pnx-cp-price]').forEach((priceBox) => {
          const variantId = priceBox.dataset.pnxCpPrice;
          const main = priceBox.querySelector('[data-pnx-cp-price-main]');
          if (!variantId || !main) return;
          const item = document.createElement('div');
          item.setAttribute('samitaWS-element', 'handle');
          item.setAttribute('p-handle', handle);
          item.setAttribute('p-variant-id', variantId);
          item.dataset.pnxCpProductId = row.dataset.productId || '';
          item.dataset.pnxCpVariantId = variantId;
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('tabindex', '-1');
          link.textContent = handle;
          const target = document.createElement('div');
          /* unit-price: Samita が卸価格HTMLを書き込む先。collection-product-price: Samita が処理時に
             samitaWS-product-main-price（true→ルール無しなら false）を付ける印の対象。この印で no-custom を確定する */
          target.setAttribute('samitaWS-product-unit-price', '');
          target.setAttribute('samitaWS-collection-product-price', '');
          target.innerHTML = main.innerHTML;
          item.appendChild(link);
          item.appendChild(target);
          scratch.appendChild(item);
        });
      });

      if (!scratch.children.length) return;
      document.body.appendChild(scratch);
      this.scratch = scratch;

      this.mirrorObserver = new MutationObserver(() => this.mirrorWholesalePrices());
      this.mirrorObserver.observe(scratch, { childList: true, subtree: true, characterData: true, attributes: true });
      this.scheduleWholesaleTimeout();

      /* Samita は scroll 契機で再走査し、作業枠を一覧モードとして処理する。初期表示（未スクロール）でも
         卸価格が出るよう、作業枠を置いた直後に一度だけ通知する */
      this.notifyPriceApps();
    }

    teardownWholesaleMirror() {
      if (this.wholesaleTimer) {
        clearTimeout(this.wholesaleTimer);
        this.wholesaleTimer = null;
      }
      if (this.mirrorObserver) {
        this.mirrorObserver.disconnect();
        this.mirrorObserver = null;
      }
      if (this.scratch && this.scratch.parentNode) {
        this.scratch.parentNode.removeChild(this.scratch);
      }
      this.scratch = null;
    }

    mirrorWholesalePrices() {
      if (!this.scratch) return;
      this.scratch.querySelectorAll('[data-pnx-cp-variant-id]').forEach((item) => this.mirrorScratchItem(item));
      this.refreshWholesaleNote();
    }

    /*
      作業枠1つ（＝1 variant）を評価して表示セルへ反映する。
      state（data-pnx-cp-unit-state）: pending → valid-custom | no-custom | invalid | timeout
      - Samita の処理完了（samitaWS-active）を確認してから解析する（ARCH Blocking 2）
      - 商品ID は出力側の値が空でなく行と完全一致（Blocking 1）。variant ID も出力側にあれば完全一致（5）。
        出力側に variant ID が無い場合は単一variant商品だけ許容（6）
      - customized-price 要素はちょうど1個（3）。価格トークンはちょうど1個（4・pnx-unit-price.js）
      - 入数・通常価格は Liquid 由来の data 属性（7）。失敗時は単価非表示（推測復帰しない）
    */
    mirrorScratchItem(item) {
      const target = item.querySelector('[samitaWS-product-unit-price]');
      if (!target) return;
      const variantId = item.dataset.pnxCpVariantId;
      const productId = item.dataset.pnxCpProductId || '';
      const boxes = Array.from(this.querySelectorAll(`[data-pnx-cp-price="${variantId}"]`)).filter(
        (box) => (box.closest('[data-pnx-cp-row]') || {}).dataset?.productId === productId
      );
      if (!boxes.length) return;

      const rendered = target.querySelector('[samitaWS-product-id]');
      if (!rendered) {
        /* Samita は卸ルールが無い要素には samitaWS-active を付けず、価格要素の
           samitaWS-product-main-price を "false" にして終える（DEV実測）。
           それが付いていれば no-custom（通常表示・Liquid単価のまま）。付いていなければ書き込み待ち（pending） */
        const mainPriceFlag = (target.getAttribute('samitaWS-product-main-price') || '').toLowerCase();
        if (mainPriceFlag === 'false') {
          boxes.forEach((box) => this.setUnitState(box, 'no-custom'));
        }
        return;
      }
      /* 卸価格HTMLが書かれている＝処理完了（samitaWS-active は summary 分岐で付与される） */

      const outProductId = (rendered.getAttribute('samitaWS-product-id') || '').trim();
      const outVariantEl = target.querySelector('[samitaWS-variant-id]');
      const outVariantId = outVariantEl ? (outVariantEl.getAttribute('samitaWS-variant-id') || '').trim() : '';
      const customized = target.querySelectorAll('.samitaWS-customized-price');
      const variantCount = parseInt(boxes[0].dataset.variantCount, 10) || 1;

      /* 卸価格が書かれていない（ルールなし）→ 通常表示へ（ミラー済みなら復元） */
      if (customized.length === 0) {
        boxes.forEach((box) => this.restoreRetailPrice(box, 'no-custom'));
        return;
      }

      /*
        価格HTMLをミラーする条件（PNX-ARCH Blocking 1・売価も fail-closed）:
        - 出力側の商品IDが空でなく行の商品IDと完全一致
        - variant 検証済み: 出力側 variant ID があれば完全一致、無ければ単一variant商品のみ
        - customized-price 要素がちょうど1個
        いずれかを満たさなければ価格もミラーせず、通常価格へ戻す（invalid）
      */
      const productVerified = !!outProductId && !!productId && outProductId === productId;
      const variantVerified = outVariantId ? outVariantId === String(variantId) : variantCount === 1;
      if (!productVerified || !variantVerified || customized.length !== 1) {
        boxes.forEach((box) => this.restoreRetailPrice(box, 'invalid'));
        return;
      }

      const html = target.innerHTML;
      boxes.forEach((box) => {
        const main = box.querySelector('[data-pnx-cp-price-main]');
        if (!main) return;
        if (box.dataset.pnxCpOriginal === undefined) box.dataset.pnxCpOriginal = main.innerHTML;
        if (box.dataset.pnxCpMirrored !== html) {
          box.dataset.pnxCpMirrored = html;
          main.innerHTML = html;
          main.classList.add('pnx-cp__price-main--wholesale');
          box.classList.add('pnx-cp__price--wholesale');
        }

        /* 特別単価: 純粋関数で検算（数値トークン1個・卸価格＜通常価格・入数は Liquid 由来の整数） */
        let result;
        if (!(window.PnxUnitPrice && window.PnxUnitPrice.evaluateWholesaleUnitPrice)) {
          result = { state: 'invalid', reason: 'module' };
        } else {
          result = window.PnxUnitPrice.evaluateWholesaleUnitPrice({
            priceText: customized[0].textContent || '',
            irisu: box.dataset.irisu,
            retailYen: box.dataset.retailYen,
          });
        }
        this.setUnitState(box, result.state, result);
      });
    }

    /* ミラー済みの卸価格HTMLを元の通常価格HTMLへ戻し、卸価格用 class を解除する（no-custom / invalid 遷移時） */
    restoreRetailPrice(box, state) {
      const main = box.querySelector('[data-pnx-cp-price-main]');
      if (main && box.dataset.pnxCpMirrored !== undefined) {
        if (box.dataset.pnxCpOriginal !== undefined) main.innerHTML = box.dataset.pnxCpOriginal;
        main.classList.remove('pnx-cp__price-main--wholesale');
        box.classList.remove('pnx-cp__price--wholesale');
        delete box.dataset.pnxCpMirrored;
      }
      this.setUnitState(box, state);
    }

    setUnitState(box, state, result) {
      box.dataset.pnxCpUnitState = state;
      const wholesaleUnit = box.querySelector('[data-pnx-cp-unit-wholesale]');
      if (!wholesaleUnit) return;
      if (state === 'valid-custom' && result && result.text) {
        /* 文言は OWNER 決定（2026-09-02）。段階価格ルールは現在0件のため注記なし。
           段階価格を使い始めた場合は注記と非表示ガードの再検討が必要（Issue #8 記録） */
        wholesaleUnit.textContent = `特別単価 ${result.text}円（税込）`;
        wholesaleUnit.hidden = false;
      } else {
        /* invalid / timeout / no-custom: 古い参考単価を残さない */
        wholesaleUnit.textContent = '';
        wholesaleUnit.hidden = true;
      }
    }

    refreshWholesaleNote() {
      const note = this.querySelector('[data-pnx-cp-note]');
      if (!note) return;
      note.hidden = !this.querySelector('[data-pnx-cp-unit-state="valid-custom"]');
    }

    /* 一定時間 Samita の処理が来なければ timeout（通常表示のまま・参考単価なし） */
    scheduleWholesaleTimeout() {
      if (this.wholesaleTimer) clearTimeout(this.wholesaleTimer);
      this.wholesaleTimer = setTimeout(() => {
        this.querySelectorAll('[data-pnx-cp-price][data-pnx-cp-unit-state="pending"]').forEach((box) => {
          this.setUnitState(box, 'timeout');
        });
        this.refreshWholesaleNote();
      }, 15000);
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-pnx-cp-row]'));
    }

    get submitButton() {
      return this.querySelector('[data-pnx-cp-submit]');
    }

    get cartDrawerElement() {
      return document.querySelector('cart-drawer');
    }

    /* ---------- 行の状態 ---------- */

    selectedVariant(row) {
      const select = row.querySelector('[data-pnx-cp-variant]');
      if (select) {
        const option = select.options[select.selectedIndex];
        if (!option || !option.value) return null;
        return {
          id: option.value,
          available: option.dataset.available === 'true' && !option.disabled,
          image: option.dataset.image || '',
        };
      }
      const single = row.querySelector('[data-pnx-cp-variant-single]');
      if (single && single.dataset.variantId) {
        return {
          id: single.dataset.variantId,
          available: single.dataset.available === 'true',
          image: '',
        };
      }
      return null;
    }

    quantityInput(row) {
      return row.querySelector('[data-pnx-cp-qty]');
    }

    sanitizeQuantity(input) {
      if (!input) return 0;
      const max = parseInt(input.max, 10) > 0 ? parseInt(input.max, 10) : 9999;
      /* 小数は切り捨て・非数／負数は0・上限超えは上限に丸める（disabled中は値を触らない） */
      let value = Math.floor(parseFloat(input.value));
      if (!Number.isFinite(value) || value < 0) value = 0;
      if (value > max) value = max;
      if (String(value) !== input.value) input.value = String(value);
      return value;
    }

    rowQuantity(row) {
      return this.sanitizeQuantity(this.quantityInput(row));
    }

    /* variant選択に合わせて価格／在庫／画像／数量欄を切り替える */
    syncRow(row) {
      const variant = this.selectedVariant(row);
      const available = !!(variant && variant.available);
      const variantId = variant ? String(variant.id) : '';

      row.querySelectorAll('[data-pnx-cp-price]').forEach((el) => {
        el.hidden = el.dataset.pnxCpPrice !== variantId;
      });
      row.querySelectorAll('[data-pnx-cp-stock]').forEach((el) => {
        el.hidden = el.dataset.pnxCpStock !== variantId;
      });

      row.classList.toggle('pnx-cp__row--soldout', !available);

      const input = this.quantityInput(row);
      row.querySelectorAll('[data-pnx-cp-minus], [data-pnx-cp-plus]').forEach((button) => {
        button.disabled = !available;
      });
      if (input) {
        input.disabled = !available;
        if (!available) input.value = '0';
        this.sanitizeQuantity(input);
      }

      if (variant && variant.image) {
        const img = row.querySelector('.pnx-cp__image img');
        if (img && img.getAttribute('src') !== variant.image) {
          img.removeAttribute('srcset');
          img.setAttribute('src', variant.image);
        }
      }
    }

    refreshSubmit() {
      const button = this.submitButton;
      if (!button) return;
      const count = this.rows.filter((row) => {
        const variant = this.selectedVariant(row);
        return variant && variant.available && this.rowQuantity(row) > 0;
      }).length;
      button.disabled = this.busy || count === 0;
    }

    /* Samita は scroll（1秒 debounce）を契機に再走査する。作業枠の設置直後に一度だけ通知する。
       variant切替時は作業枠が全variant分あるため通知不要（ミラー済みHTMLを表示切替するだけ） */
    notifyPriceApps() {
      if (window.Samita && window.Samita.Wholesale) {
        window.dispatchEvent(new Event('scroll'));
      }
    }

    /* ---------- イベント ---------- */

    handleClick(event) {
      const minus = event.target.closest('[data-pnx-cp-minus]');
      const plus = event.target.closest('[data-pnx-cp-plus]');
      const submit = event.target.closest('[data-pnx-cp-submit]');

      if (minus || plus) {
        const row = event.target.closest('[data-pnx-cp-row]');
        const input = row && this.quantityInput(row);
        if (!input || input.disabled) return;
        const current = this.sanitizeQuantity(input);
        input.value = String(plus ? current + 1 : Math.max(0, current - 1));
        this.sanitizeQuantity(input);
        this.clearMessages();
        this.refreshSubmit();
        return;
      }

      if (submit) {
        event.preventDefault();
        this.addSelectedToCart();
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-pnx-cp-variant]')) {
        const row = event.target.closest('[data-pnx-cp-row]');
        if (row) this.syncRow(row);
        this.clearMessages();
        this.refreshSubmit();
        return;
      }
      if (event.target.matches('[data-pnx-cp-qty]')) {
        this.sanitizeQuantity(event.target);
        this.clearMessages();
        this.refreshSubmit();
      }
    }

    handleInput(event) {
      if (event.target.matches('[data-pnx-cp-qty]')) {
        const value = parseInt(event.target.value, 10);
        const button = this.submitButton;
        if (!button) return;
        if (Number.isFinite(value) && value > 0) {
          button.disabled = this.busy;
        } else {
          this.refreshSubmit();
        }
      }
    }

    handleKeydown(event) {
      if (event.key === 'Enter' && event.target.matches('[data-pnx-cp-qty]')) {
        event.preventDefault();
        this.sanitizeQuantity(event.target);
        this.refreshSubmit();
        const button = this.submitButton;
        if (button && !button.disabled) this.addSelectedToCart();
      }
    }

    /* ---------- メッセージ ---------- */

    clearMessages() {
      this.querySelectorAll('[data-pnx-cp-status], [data-pnx-cp-error]').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
      this.rows.forEach((row) => row.classList.remove('pnx-cp__row--error'));
    }

    showStatus(message) {
      const el = this.querySelector('[data-pnx-cp-status]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    showError(message, failedTitles = []) {
      const el = this.querySelector('[data-pnx-cp-error]');
      if (el) {
        el.textContent = message;
        el.hidden = !message;
      }
      this.rows.forEach((row) => {
        const title = row.dataset.productTitle || '';
        row.classList.toggle('pnx-cp__row--error', failedTitles.includes(title));
      });
    }

    /* ---------- カート追加 ---------- */

    collectItems() {
      return this.rows
        .map((row) => {
          const variant = this.selectedVariant(row);
          const quantity = this.rowQuantity(row);
          if (!variant || !variant.available || quantity <= 0) return null;
          return { id: variant.id, quantity, title: row.dataset.productTitle || '', row };
        })
        .filter(Boolean);
    }

    setLoading(isLoading) {
      const button = this.submitButton;
      this.busy = isLoading;
      if (!button) return;
      button.classList.toggle('btn--loading', isLoading);
      if (isLoading) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
      this.querySelectorAll('[data-pnx-cp-qty], [data-pnx-cp-variant]').forEach((el) => {
        if (isLoading) {
          el.dataset.pnxCpWasDisabled = el.disabled ? 'true' : 'false';
          el.disabled = true;
        } else if (el.dataset.pnxCpWasDisabled !== undefined) {
          el.disabled = el.dataset.pnxCpWasDisabled === 'true';
          delete el.dataset.pnxCpWasDisabled;
        }
      });
      this.refreshSubmit();
    }

    addSelectedToCart() {
      if (this.busy) return;
      this.clearMessages();

      const items = this.collectItems();
      if (items.length === 0) {
        this.showError(this.dataset.msgNone || '');
        this.refreshSubmit();
        return;
      }
      const payloadItems = items.map((item) => ({ id: item.id, quantity: item.quantity }));

      const routes = window.FoxTheme && window.FoxTheme.routes;
      const settings = (window.FoxTheme && window.FoxTheme.settings) || {};
      const utils = window.FoxTheme && window.FoxTheme.utils;
      if (!routes || !routes.cart_add_url || !utils) {
        this.showError(this.dataset.msgError || '');
        return;
      }

      /* カートページ表示中、またはドロワー／ポップアップ以外のカートタイプは既存と同じくフォームPOSTで遷移 */
      if (
        document.body.classList.contains('cart-template') ||
        (settings.cartType !== 'drawer' && settings.cartType !== 'popup')
      ) {
        this.setLoading(true);
        utils.postLink(routes.cart_add_url, { parameters: { items: payloadItems } });
        return;
      }

      this.setLoading(true);
      this.lastClickedElement = this.submitButton;

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items: payloadItems,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_add_url}`, { ...utils.fetchConfig('javascript'), ...{ body } })
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            await this.handleAddError(parsedState, items);
            return;
          }
          const cartJson = await (await fetch(`${routes.cart_url}`, { ...utils.fetchConfig() })).json();
          cartJson['sections'] = parsedState['sections'];

          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: parsedState },
            })
          );

          const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
          /* 追加した行だけ数量を0へ戻す（送信中は入力欄が一時的にdisabledなので状態に関係なく戻す） */
          items.forEach((item) => {
            const input = this.quantityInput(item.row);
            if (input) input.value = '0';
          });
          this.showStatus((this.dataset.msgAdded || '').replace('[count]', String(totalQuantity)));

          if (settings.cartType === 'popup' && window.PnxAddedModal) {
            window.PnxAddedModal.show();
          } else if (this.cartDrawerElement) {
            this.cartDrawerElement.show(this.lastClickedElement);
          }
        })
        .catch(async () => {
          /* 通信結果が不明: カートは変更済みかもしれないので実カートへ同期し、数量は残す */
          await this.syncCartState();
          this.showError(this.joinMessages(this.dataset.msgError, this.dataset.msgPartial), items.map((i) => i.title));
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    /* 実カート（/cart）を再取得し、ヘッダー件数・ドロワーを実状態へ同期する（既存 ProductForm のエラー時経路と同じ） */
    async syncCartState() {
      const routes = window.FoxTheme && window.FoxTheme.routes;
      const utils = window.FoxTheme && window.FoxTheme.utils;
      if (!routes || !utils) return;
      try {
        const cartJson = await (await fetch(`${routes.cart_url}`, { ...utils.fetchConfig('json', 'GET') })).json();
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        if (this.cartDrawerElement) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        }
      } catch (error) {
        /* 同期にも失敗した場合はメッセージだけ残す（数量は保持） */
      }
    }

    joinMessages(...parts) {
      return parts.filter((part) => typeof part === 'string' && part.trim() !== '').join(' ');
    }

    async handleAddError(parsedState, items) {
      /*
        Shopify の cart/add は在庫超過時、追加可能な数量をカートへ入れたうえで 422 を返すことがある
        ＝「エラー＝カート未変更」ではない。実カートを再取得して件数・表示を同期し、数量はリセットしない。
        顧客向けメッセージ（description）を表示し、内部IDは出さない。
      */
      const description = typeof parsedState.description === 'string' ? parsedState.description : '';
      await this.syncCartState();

      /* 行の特定は商品名が一意に一致したときだけ。特定できなければ送信対象の全行を候補として示す */
      const matched = items.filter((item) => item.title && description.includes(item.title));
      const failedTitles = matched.length === 1 ? [matched[0].title] : items.map((item) => item.title);

      const message = this.joinMessages(description || this.dataset.msgError, this.dataset.msgPartial);
      this.showError(message, failedTitles);
      document.dispatchEvent(
        new CustomEvent('product-ajax:error', {
          detail: { errorMessage: message },
        })
      );
    }
  }

  customElements.define('pnx-complementary-purchase', PnxComplementaryPurchase);
}
